import React, {
  RefObject,
  useMemo,
  useState,
  useCallback,
  SyntheticEvent,
  DetailedHTMLProps,
  useEffect,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import PatientIcon from '@mui/icons-material/PersonOutlineRounded';
import CloseIcon from '@mui/icons-material/Close';
import AddContactIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from 'core/icons/delete';
import clsx from 'clsx';
import values from 'lodash/values';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import noop from 'lodash/noop';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import {
  formatPhone,
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import CalendarIcon from 'core/icons/calendar';
import SearchIcon from 'core/icons/search';
import ToolTip from 'core/components/Tooltip';
import useSearchPatients from 'pages/Dashboard/hooks/usePatients';
import {
  useClearRecentSearches,
  useRecentSearches,
  useRecordChartAccess,
} from 'pages/Dashboard/hooks/useRecentSearches';
import useEMRAccess from 'views/EMR/hooks/useEMRAccess';
import AutoCompletedInput from 'core/components/AutoComplete';
import Spinner from 'pages/Dashboard/components/Spinner';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Input from 'pages/Dashboard/components/Input';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import InsuranceIcon from 'core/icons/insurance';
import Name from 'pages/Dashboard/components/Name';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  formatDate,
} from 'utils/date';
import ListboxDropdown from 'views/EMR/components/ListboxDropdown';
import {
  PatientInfoTags,
} from 'pages/Dashboard/components/PatientInfo';
import compact from 'lodash/compact';
import {
  getPatientAge,
} from 'utils/misc';

type Part = 'root' | 'select' | 'input';

type Props = {
  className?: string;
  clearOnBlur?: boolean;
  id: string;
  value?: PatientCompact | null;
  label?: string | ReactNode | false;
  placeholder: string;
  readonly?: boolean;
  onChange: (value: PatientCompact | null) => void;
  hideDateOfBirth?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  resetOnEmpty?: boolean;
  allowDefault?: boolean;
  ignoreIdsSet?: Set<number>;
  required?: boolean;
  classes?: Partial<Record<Part, string>>;
  error?: boolean;
  onAddPatient?: (phoneNumber: string) => void;
  disabled?: boolean;
  containerRef?: RefObject<HTMLElement>;
  blurOnSelect?: boolean;
  disablePortal?: boolean;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  recentSearchEnabled?: boolean;
  onFocus?: (e: SyntheticEvent) => void;
  onBlur?: (e: SyntheticEvent) => void;
};

type AdornmentProps = {
  value?: PatientCompact | null;
  isEmpty?: boolean;
  isLoading?: boolean;
  readonly?: boolean;
  allowDefault?: boolean;
  hideDateOfBirth?: boolean;
  onChange: (value: PatientCompact | null) => void;
  setInputValue: (value: string) => void;
};

type RowProps = DetailedHTMLProps<any, any> & {
  patient: PatientCompact;
  optionsCount: number;
  onClick: () => void;
};

function PatientRow({
  patient,
  props,
}: RowProps) {
  const phoneNumber = typeof patient?.phoneNumber === 'string'
    ? patient?.phoneNumber
    : patient?.phoneNumber?.phoneNumberWithAreaCode;

  return (
    <div
      {...props}
      key={patient?.patientId}
      className={clsx(
        'cursor-pointer p-2 flex font-semibold items-start justify-between hover:text-blue-500 border-b border-b-gray-200 nw-patient-information',
        props?.className,
      )}
    >
      <Name
        name={getDisplayName(patient?.formattedNames)}
        image={undefined}
      >
        <div className="font-semibold text-xs text-gray-400 flex gap-px items-center">
          <PatientIcon className="!w-4 !h-4 -ml-px" />
          {compact([
            patient?.formattedDateOfBirth,
            patient?.isDeceased ? null : getPatientAge(patient?.dateOfBirth ?? ''),
            patient?.genderDescription,
            notEmptyString(phoneNumber) ? formatPhone(phoneNumber) : null,
          ]).join(' • ')}
        </div>
        {notEmptyString(patient?.insuranceName) && (
          <div className="font-semibold text-xs text-gray-400 flex gap-px items-center">
            <InsuranceIcon className="!w-4 !h-4 -ml-px" />
            {patient?.insuranceName}
          </div>
        )}
      </Name>
      <PatientInfoTags isDeceased={patient?.isDeceased} isInactive={patient?.isInactive} />
    </div>
  );
}

function InputEndAdornment({
  value,
  isEmpty = false,
  hideDateOfBirth,
  readonly = false,
  allowDefault,
  onChange,
  setInputValue,
  isLoading,
}: AdornmentProps) {
  return (
    <InputAdornment position="end">
      <div className="flex items-center gap-2">
        {!isEmpty && (
          <>
            {!hideDateOfBirth && (
              <ToolTip title="Date of Birth">
                <div className="flex items-center gap-2 justify-end text-xs font-semibold cursor-pointer">
                  {value?.formattedDateOfBirth ?? formatDate(value?.dateOfBirth ?? '')}
                  <CalendarIcon className="!w-4 !h-4 !text-primaryLight" />
                </div>
              </ToolTip>
            )}
            {(!readonly && allowDefault) && (
              <IconButton
                onClick={() => {
                  onChange(null);
                  setInputValue('');
                }}
                className="!p-0"
              >
                <CloseIcon className="!w-4 !h-4" />
              </IconButton>
            )}
          </>
        )}
        <IconButton
          onClick={noop}
          className="!p-0"
        >
          {(!isLoading && !readonly) && <SearchIcon className="!w-4 !h-4" />}
          {isLoading && <Spinner size={20} />}
        </IconButton>
      </div>
    </InputAdornment>
  );
}

type RecentSearchesHeaderProps = {
  shouldShow: boolean;
  hasRecentSearches: boolean;
  isClearing: boolean;
  onClear: () => void;
};

function RecentSearchesHeader({
  shouldShow,
  hasRecentSearches,
  isClearing,
  onClear,
}: RecentSearchesHeaderProps) {
  return shouldShow && hasRecentSearches ? (
    <div className="flex items-center justify-between pb-1.5 select-none">
      <span className="text-xs font-medium text-gray-600">
        Recents
      </span>
      <ToolTip title="Clear history">
        <IconButton
          size="small"
          disabled={isClearing}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClear();
          }}
          aria-label="Clear history"
          className="!p-1 !text-gray-500"
        >
          <DeleteIcon className="!w-5 !h-5 !text-gray-600" />
        </IconButton>
      </ToolTip>
    </div>
  ) : null;
}

export default function PatientSelect(props: Props) {
  const {
    className,
    id,
    onChange,
    placeholder,
    value,
    readonly,
    label = 'Patient',
    inputRef,
    ignoreIdsSet = new Set<number>(),
    hideDateOfBirth = false,
    allowDefault = false,
    required = false,
    classes,
    error,
    clearOnBlur = false,
    onAddPatient,
    disabled = false,
    containerRef: _containerRef,
    blurOnSelect = false,
    inputProps,
    recentSearchEnabled = false,
    ...field
  } = props;
  const [inputValue, setInputValue] = useState<string>('');
  const [searchQueryChanged, setSearchQueryChanged] = useState<boolean>(false);
  const [page, setPage] = useState(0);
  const [pageChanged, setPageChanged] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const wrapperClasses = clsx('flex flex-col flex-auto', {
    '!space-y-0': isEmptyString(label),
    'space-y-1': !isEmptyString(label),
  });
  const containerClasses = clsx('flex flex-wrap w-full gap-4 mb-3', className, classes?.root);

  const { canUseRecentSearches: canUseRecentSearchesForOrg, userInfo } = useEMRAccess();
  // Wait for user info so restricted orgs never fetch or show recent searches while it loads.
  const canUseRecentSearches = canUseRecentSearchesForOrg && !isNil(userInfo);
  const shouldEnableRecentSearches = recentSearchEnabled && canUseRecentSearches && isEmptyString(inputValue);
  const shouldShowRecentSearches = recentSearchEnabled && canUseRecentSearches && isFocused && isEmptyString(inputValue);

  const {
    data: recentSearchesData = [],
    isFetching: isFetchingRecentSearches,
  } = useRecentSearches(shouldEnableRecentSearches);
  const { mutate: recordChartAccess } = useRecordChartAccess();
  const {
    mutate: clearRecentSearches,
    isPending: isClearingRecentSearches,
  } = useClearRecentSearches();

  const {
    data,
    refetch,
    isFetching,
    isFetched,
  } = useSearchPatients(
    inputValue,
    0,
    (page + 1) * 25,
    open && !shouldShowRecentSearches,
  );

  const { data: _options = [], pageProperties } = data ?? {};

  useEffect(() => {
    if (page > 0) {
      refetch();
    }
  }, [page]);

  const isSearching = useMemo(() => (
    isFetching && searchQueryChanged
  ), [isFetching, searchQueryChanged]);

  const options = useMemo(() => {
    if (isSearching) {
      return [];
    }
    return (
      ignoreIdsSet.size > 0
        ? _options.filter((option) => !ignoreIdsSet.has(option?.patientId ?? 0))
        : _options
    );
  }, [_options, ignoreIdsSet]);

  const recentSearchesOptions = useMemo(() => (
    shouldShowRecentSearches ? recentSearchesData : []
  ), [shouldShowRecentSearches, recentSearchesData]);

  const displayOptions = shouldShowRecentSearches ? recentSearchesOptions : options;

  useEffect(() => {
    if (isFetched) {
      setSearchQueryChanged(false);
      setPageChanged(false);
    }
  }, [isFetched]);

  const isLoadingMore = useMemo(() => (
    isFetching && pageChanged
  ), [isFetching, pageChanged]);

  const onClose = () => {
    setOpen(false);
    setIsFocused(false);
  };

  const onOpen = () => {
    setOpen(true);
    setIsFocused(true);
  };

  const nextPage = useCallback(() => {
    setPageChanged(true);
    setPage(page + 1);
  }, [page, setPage]);

  const handleChange = (val: PatientCompact | PatientCompact[]) => {
    setOpen(false);
    const selected = Array.isArray(val) ? val[0] : val;
    if (selected?.patientId && canUseRecentSearches) {
      recordChartAccess(selected.patientId);
    }
    onChange(selected);
    setInputValue('');
  };

  const handleInputChange = useCallback(debounce((event: SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    setPage(0);
  }, 700), []);

  const recentSearchesHeader = useMemo(() => (
    <RecentSearchesHeader
      shouldShow={shouldShowRecentSearches}
      hasRecentSearches={recentSearchesData.length > 0}
      isClearing={isClearingRecentSearches}
      onClear={() => {
        if (canUseRecentSearches) {
          clearRecentSearches();
        }
      }}
    />
  ), [shouldShowRecentSearches, recentSearchesData, isClearingRecentSearches, clearRecentSearches, canUseRecentSearches]);

  const noOptionsText = useMemo(() => {
    if (shouldShowRecentSearches) {
      if (isFetchingRecentSearches) {
        return 'Loading...';
      }
      return 'Start typing to search for a patient.';
    }
    return isEmptyString(inputValue)
      ? 'Start typing to see patients'
      : 'No Patients Found';
  }, [shouldShowRecentSearches, isFetchingRecentSearches, inputValue]);

  useEffect(() => {
    if (isEmptyString(inputValue) && !allowDefault) {
      onChange(null);
    }
  }, [inputValue, allowDefault]);

  const isEmpty = useMemo(() => (
    isNil(value) || values(value).every(isEmptyString)
  ), [value]);

  const currentLoading = shouldShowRecentSearches ? isFetchingRecentSearches : isFetching;

  const CustomPaper = useCallback(({ children, className, ...paperProps }: any) => (
    <div
      className={clsx(
        'MuiPaper-root MuiPaper-elevation MuiPaper-rounded MuiPaper-elevation1 MuiAutocomplete-paper !bg-white bg-white text-gray-900 shadow-xl border border-gray-200 relative z-[9999]',
        className,
      )}
      {...paperProps}
    >
      {recentSearchesHeader}
      {children}
    </div>
  ), [recentSearchesHeader]);

  return (
    <div className="relative flex gap-3 items-end w-full">
      <div className={containerClasses}>
        <Wrapper
          id={id}
          error={error}
          showErrorText={false}
          className={wrapperClasses}
          label={typeof label === 'boolean' ? null : label}
          required={required}
          labelClasses="font-semibold text-xs text-gray-500"
        >
          {readonly
            ? (
              <Input
                id={id}
                onChange={noop}
                className={clsx('flex flex-1 space-y-1', classes?.input)}
                inputProps={{ readOnly: true, ...inputProps }}
                value={!isNil(value) ? getDisplayName(value?.formattedNames) : placeholder}
                disabled
                endIcon={(
                  <InputEndAdornment
                    readonly
                    value={value}
                    onChange={onChange}
                    setInputValue={setInputValue}
                    hideDateOfBirth={hideDateOfBirth}
                  />
                )}
              />
            ) : (
              <AutoCompletedInput<PatientCompact>
                {...field}
                id={id}
                disabled={disabled}
                options={displayOptions ?? []}
                onOpen={onOpen}
                onClose={onClose}
                onFocus={(e) => {
                  setIsFocused(true);
                  field?.onFocus?.(e);
                }}
                onBlur={(e) => {
                  setIsFocused(false);
                  field?.onBlur?.(e);
                }}
                open={open && (!currentLoading || pageChanged || shouldShowRecentSearches)}
                disableCloseOnSelect
                getLabel={(option) => getDisplayName(option?.formattedNames)}
                renderOption={(props, option) => (
                  <PatientRow
                    key={option.patientId}
                    patient={option}
                    props={props}
                  />
                )}
                slots={{
                  paper: CustomPaper,
                }}
                slotProps={{
                  listbox: {
                    component: ListboxDropdown,
                    isLoadingMore,
                    fetchNextPage: nextPage,
                    isLoading: currentLoading,
                    pageProperties,
                  },
                } as Object}
                loading={currentLoading}
                inputRef={inputRef}
                value={allowDefault && !isEmpty ? value : null}
                openOnFocus
                includeInputInList
                filterSelectedOptions={false}
                className={clsx('w-full', classes?.select)}
                onChange={handleChange}
                filterOptions={(x) => x}
                placeholder={placeholder}
                onInputChange={handleInputChange}
                blurOnSelect={blurOnSelect}
                clearOnBlur={clearOnBlur}
                inputProps={inputProps}
                noOptionsText={noOptionsText}
                isOptionEqualToValue={(a, b) => a?.patientId === (b as PatientCompact)?.patientId}
                dropdownClassName="!max-w-lg !overflow-hidden mt-px p-2"
                InputProps={{
                  classes: { root: clsx('!pr-2', classes?.input) },
                  endAdornment: (
                    <InputEndAdornment
                      value={value}
                      allowDefault={allowDefault}
                      isEmpty={isEmpty}
                      isLoading={currentLoading}
                      onChange={onChange}
                      setInputValue={setInputValue}
                      hideDateOfBirth={hideDateOfBirth}
                    />
                  ),
                }}
              />
            )}
        </Wrapper>
      </div>
      {!isNil(onAddPatient) && (
        <ToolTip
          title="Add Patient"
          aria-label="Add Patient"
        >
          <IconButton
            color="info"
            size="small"
            onClick={() => {
              const query = inputRef?.current?.value ?? '';
              const phoneNumber = query.replace(/\+|\(|\)|-|\.|\s/gm, '');
              onAddPatient?.(phoneNumber);
            }}
            className="nw-create-patient"
          >
            <AddContactIcon
              className={clsx('!w-5 !h-5', { 'text-blue-500': !disabled })}
            />
          </IconButton>
        </ToolTip>
      )}
    </div>
  );
}
